import { motion } from 'framer-motion';
import statistics from '../data/statistics';

// Circular Progress Component - Motion ile güncellendi
const CircularProgress = ({ percentage, size = 120, strokeWidth = 8, color = "text-navy" }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-gray-200"
        />
        {/* Motion Circle: Dolum animasyonu */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }} // Başlangıçta boş
          whileInView={{ strokeDashoffset: offset }}    // Görününce dol
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          strokeLinecap="round"
          className={color}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.span 
          initial={{ opacity: 0 }} 
          whileInView={{ opacity: 1 }} 
          transition={{ delay: 0.5 }}
          className={`text-2xl font-bold ${color}`}
        >
            {percentage}%
        </motion.span>
      </div>
    </div>
  );
};

export default function Statistics() {
  const statCards = [
    { title: "Toplam Yayın", value: statistics.publications.total, icon: "📚", color: "text-blue-600", bgColor: "bg-blue-50", borderColor: "border-blue-200", description: `${statistics.publications.sci} SCI, ${statistics.publications.national} Ulusal, ${statistics.publications.conferences} Bildiri` },
    { title: "Projeler", value: statistics.projects.total, icon: "🔬", color: "text-emerald-600", bgColor: "bg-emerald-50", borderColor: "border-emerald-200", description: `${statistics.projects.ongoing} Devam Eden, ${statistics.projects.completed} Tamamlanan` },
    { title: "Öğrenciler", value: statistics.students.masters + statistics.students.phd, icon: "👥", color: "text-purple-600", bgColor: "bg-purple-50", borderColor: "border-purple-200", description: `${statistics.students.masters} Yüksek Lisans, ${statistics.students.phd} Doktora` },
    { title: "H-Index", value: statistics.hIndex, icon: "📊", color: "text-amber-600", bgColor: "bg-amber-50", borderColor: "border-amber-200", description: "Akademik Etki" },
    { title: "Atıf Sayısı", value: statistics.citations, icon: "⭐", color: "text-rose-600", bgColor: "bg-rose-50", borderColor: "border-rose-200", description: "Toplam Atıf" },
    { title: "Deneyim", value: statistics.yearsOfExperience, icon: "🎓", color: "text-indigo-600", bgColor: "bg-indigo-50", borderColor: "border-indigo-200", description: "Yıl" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-serif font-bold text-navy mb-4 tracking-tight"
          >
            İstatistikler
          </motion.h1>
          <motion.div 
            initial={{ width: 0 }} animate={{ width: "6rem" }}
            className="h-1 bg-gradient-to-r from-transparent via-navy to-transparent mx-auto mb-4"
          ></motion.div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Akademik kariyerimden öne çıkan rakamlar ve başarılar
          </p>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {statCards.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className={`bg-white rounded-xl border-2 ${stat.borderColor} p-8 hover:shadow-xl transition-all duration-300 group`}
            >
              <div className="flex items-start justify-between mb-6">
                <div className={`${stat.bgColor} w-14 h-14 rounded-lg flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300`}>
                  {stat.icon}
                </div>
                <div className="text-right">
                  <div className={`text-4xl font-bold ${stat.color} mb-1`}>
                    {stat.value}
                  </div>
                  <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    {stat.title}
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-600 leading-relaxed">{stat.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Detailed Analysis Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          
          {/* Publications Breakdown */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl border-2 border-gray-200 p-8 hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-8">
              <div><h3 className="text-2xl font-serif font-bold text-navy mb-2">Yayın Dağılımı</h3><p className="text-sm text-gray-500">Toplam {statistics.publications.total} yayın</p></div>
              <div className="bg-blue-50 w-16 h-16 rounded-xl flex items-center justify-center text-3xl">📚</div>
            </div>
            
            <div className="space-y-6">
              {[
                { label: 'SCI/SCI Expanded', count: statistics.publications.sci, color: 'bg-blue-600', total: statistics.publications.total },
                { label: 'Ulusal Dergiler', count: statistics.publications.national, color: 'bg-emerald-600', total: statistics.publications.total },
                { label: 'Konferans Bildirileri', count: statistics.publications.conferences, color: 'bg-purple-600', total: statistics.publications.total }
              ].map((item, idx) => (
                <div key={idx}>
                    <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                            <span className="text-gray-700 font-medium">{item.label}</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500">{Math.round((item.count / item.total) * 100)}%</span>
                            <span className={`font-bold text-lg ${item.color.replace('bg-', 'text-')}`}>{item.count}</span>
                        </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                        <motion.div 
                            className={`${item.color} h-2.5 rounded-full`}
                            initial={{ width: 0 }}
                            whileInView={{ width: `${(item.count / item.total) * 100}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                        ></motion.div>
                    </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Projects Breakdown */}
          <motion.div 
             initial={{ opacity: 0, x: 30 }}
             whileInView={{ opacity: 1, x: 0 }}
             viewport={{ once: true }}
             className="bg-white rounded-xl border-2 border-gray-200 p-8 hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-8">
              <div><h3 className="text-2xl font-serif font-bold text-navy mb-2">Proje Durumu</h3><p className="text-sm text-gray-500">Toplam {statistics.projects.total} proje</p></div>
              <div className="bg-emerald-50 w-16 h-16 rounded-xl flex items-center justify-center text-3xl">🔬</div>
            </div>
            
            <div className="space-y-6">
               {[
                { label: 'Tamamlanan', count: statistics.projects.completed, color: 'bg-emerald-600', total: statistics.projects.total },
                { label: 'Devam Eden', count: statistics.projects.ongoing, color: 'bg-amber-600', total: statistics.projects.total },
              ].map((item, idx) => (
                <div key={idx}>
                    <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                            <span className="text-gray-700 font-medium">{item.label}</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500">{Math.round((item.count / item.total) * 100)}%</span>
                            <span className={`font-bold text-lg ${item.color.replace('bg-', 'text-')}`}>{item.count}</span>
                        </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                        <motion.div 
                            className={`${item.color} h-2.5 rounded-full`}
                            initial={{ width: 0 }}
                            whileInView={{ width: `${(item.count / item.total) * 100}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                        ></motion.div>
                    </div>
                </div>
              ))}
            </div>
            
            {/* Circular Progress for Projects */}
            <div className="mt-8 pt-8 border-t border-gray-100">
              <div className="flex items-center justify-center">
                <CircularProgress 
                  percentage={Math.round((statistics.projects.completed / statistics.projects.total) * 100)} 
                  size={140}
                  strokeWidth={10}
                  color="text-emerald-600"
                />
              </div>
              <p className="text-center mt-4 text-sm text-gray-600 font-medium">Tamamlanma Oranı</p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}